/**
 * One Tally sync record — what the agent did with it, what Tally said, and the retry.
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero (through `components/detail-parts.tsx`'s `PageHero`);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks in the record card;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/tracking-overview.tsx
 *   the bordered callout under Tally's response.
 *
 * The screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState } from "react";
import { Link, useLoaderData, useOutletContext, useRevalidator, type LoaderFunctionArgs } from "react-router-dom";
import {
  PiArrowClockwiseBold as RetryIcon,
  PiCalendarPlusDuotone as QueuedIcon,
  PiChatCircleTextDuotone as ResponseIcon,
  PiCheckCircleDuotone as SentIcon,
  PiClockCounterClockwiseDuotone as AttemptIcon,
  PiHashDuotone as ReferenceIcon,
  PiInfoDuotone as InfoIcon,
  PiListNumbersDuotone as AttemptsIcon,
  PiReceiptDuotone as RecordIcon,
  PiTagDuotone as TypeIcon,
  PiTimerDuotone as TimerIcon,
  PiTrayDuotone as Inbox,
  PiWarningDuotone as WarningIcon,
} from "react-icons/pi";
import { Alert, Badge, Button, Text } from "rizzui";
import { ComplianceStatusBadge, ErrorSurface, VerbatimErrorText, WidgetCard } from "@steelyard/theme";
import { CardTitle, InfoBlock, PageHero } from "../../../components/detail-parts";
import { can } from "../../../lib/permissions";
import {
  checkQueuedInvoiceIrn,
  formatMoment,
  syncItemByKey,
  isPastLatencyFloor,
  LATENCY_FLOOR_MINUTES,
  queueBadgeState,
  retrySyncItem,
  RECORD_LOOKUP_TAKE,
  type InvoiceIrnCheck,
  syncItemLabel,
  type SyncQueueItem,
} from "../../../lib/services/tallySync";
import { invoiceDefectBanner } from "../../invoicing/columns";
import { listInvoices } from "../../../lib/services/invoicing";
import type { AccountSummary, PermissionState } from "../../../types";

/**
 * Ported from apps/admin/app/(app)/integrations/tally/queue/[id]/page.tsx. Addressed by the
 * record's number (`SYN-000031`, TallySync's by-number read). A legacy id URL is found in the queue
 * window and redirected; one outside it is reported plainly rather than as "not found", since the
 * identifier may simply be older than the window that was asked for.
 *
 * T019 parity with Queue.tsx: a single record's own BR-07 check, not the list screen's unfiltered
 * 200-row pull. `invoiceNumber` is a server-side `.Contains`, not exact — `checkQueuedInvoiceIrn`
 * keys off the exact reference via `Map.get`, so an unrelated substring match in the same page
 * can never be mistaken for this record.
 */
export async function queueDetailLoader({ params, request }: LoaderFunctionArgs) {
  const item = await syncItemByKey(request, params.itemNumber!);
  if (!item || item.entityType !== "invoice" || !item.entityReference) {
    return { item, check: null as InvoiceIrnCheck | null };
  }
  const invoicePage = await listInvoices({ page: 1, pageSize: 50, invoiceNumber: item.entityReference });
  const invoicesByNumber = new Map(invoicePage.items.map((invoice) => [invoice.invoiceNumber, invoice]));
  return { item, check: checkQueuedInvoiceIrn(item, invoicesByNumber) };
}

const BREADCRUMB_ROOT = [
  { name: "Home", href: "/" },
  { name: "Tally Agent", href: "/integrations/tally" },
  { name: "Sync Queue", href: "/integrations/tally/queue" },
];
const BACK = { href: "/integrations/tally/queue", label: "Back to the sync queue" };

export default function SyncQueueItemPage() {
  const { item, check } = useLoaderData() as { item: SyncQueueItem | null; check: InvoiceIrnCheck | null };
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const revalidator = useRevalidator();
  const [retrying, setRetrying] = useState(false);
  const [justRetried, setJustRetried] = useState(false);
  const [retryError, setRetryError] = useState<unknown>(null);

  if (!item) {
    return (
      <div className="flex flex-col gap-6">
        <PageHero breadcrumb={[...BREADCRUMB_ROOT, { name: "Sync Record" }]} back={BACK} icon={<Inbox aria-hidden className="size-9" />} iconClassName="bg-gray-100 text-gray-500" title="Sync Record" />
        <ErrorSurface
          error={{
            title: "This record isn't in the recent queue list",
            detail: `Only the most recent ${RECORD_LOOKUP_TAKE} sync records are shown here. This identifier isn't among them, so it may simply be older than what's currently loaded rather than missing.`,
          }}
          action="show this sync record"
        />
      </div>
    );
  }

  const badge = queueBadgeState(item.status);
  const mayRetry = can(permissionState, "integrations.administer");
  // The API refuses a retry on an item already `sent` (DA-01: retrying one is how a voucher gets posted twice).
  const retryable = item.status !== "sent";
  const late = isPastLatencyFloor(item.queuedAt);

  async function handleRetry() {
    setRetrying(true);
    setRetryError(null);
    try {
      await retrySyncItem(item!.itemId);
      setJustRetried(true);
      revalidator.revalidate();
    } catch (err) {
      setRetryError(err);
    } finally {
      setRetrying(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHero
        breadcrumb={[...BREADCRUMB_ROOT, { name: syncItemLabel(item) ?? "Sync record" }]}
        back={BACK}
        icon={<RecordIcon aria-hidden className="size-9" />}
        iconClassName={item.lastError ? "bg-red" : item.status === "sent" ? "bg-green" : "bg-secondary"}
        title={syncItemLabel(item) ?? "Sync Record"}
        copyValue={syncItemLabel(item) ?? undefined}
        description="What the connector agent has done with this record, and what Tally said about it."
        meta={
          <>
            {badge ? <ComplianceStatusBadge subject="tally" state={badge} /> : <Badge variant="outline" rounded="pill" className="border-muted font-mono">{item.status}</Badge>}
            <Badge variant="flat" color="secondary" rounded="pill" className="gap-1.5 font-mono">
              <TypeIcon aria-hidden className="size-4" />
              {item.entityType}
            </Badge>
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <AttemptsIcon aria-hidden className="size-4" />
              {Number.isNaN(item.attempts) ? "Attempts not reported" : `${item.attempts} ${item.attempts === 1 ? "attempt" : "attempts"}`}
            </Badge>
            {late ? (
              <Badge variant="flat" color="danger" rounded="pill" className="gap-1.5">
                <TimerIcon aria-hidden className="size-4" />
                Over {LATENCY_FLOOR_MINUTES}m in the queue
              </Badge>
            ) : null}
          </>
        }
        actions={
          mayRetry && retryable ? (
            <Button type="button" onClick={handleRetry} disabled={retrying} isLoading={retrying}>
              <RetryIcon aria-hidden className="me-1.5 size-4" />
              {retrying ? "Retrying…" : "Retry now"}
            </Button>
          ) : null
        }
      />

      {check?.status === "defect" ? invoiceDefectBanner(check.invoice) : null}
      {check?.status === "unknown" ? (
        <Alert color="warning" variant="flat" bar icon={<WarningIcon aria-hidden className="size-5" />}>
          <p className="text-sm">
            This invoice&rsquo;s IRN status could not be verified — its reference wasn&rsquo;t found among the invoices
            checked. This is not a statement that the invoice is fine.
          </p>
        </Alert>
      ) : null}

      {retryError ? <ErrorSurface error={retryError} action="retry this record" /> : null}

      {justRetried ? (
        <Alert color="success" variant="flat" bar icon={<SentIcon aria-hidden className="size-5" />}>
          <div role="status" className="flex flex-col gap-1">
            <p className="font-semibold text-green-dark">The retry was accepted.</p>
            <p className="text-sm">
              The record now reads <span className="font-mono">{item.status}</span> and has{" "}
              {Number.isNaN(item.attempts) ? "an unreported number of" : item.attempts} attempts against it. The attempt count
              is not reset by a retry.
            </p>
          </div>
        </Alert>
      ) : null}

      {/* T018 (FR-012, GAP-1 closed 2026-09-14): the real `queuedAt` (not "Last attempt", which
          stays null until the first attempt) is what makes this evaluable now. */}
      {late ? (
        <Alert color="warning" variant="flat" bar icon={<TimerIcon aria-hidden className="size-5" />}>
          <p className="text-sm">
            This record has been queued for longer than the 15-minute latency floor and has not yet reached Tally.
          </p>
        </Alert>
      ) : null}

      <div className="grid items-start gap-6 @container xl:grid-cols-12">
        <WidgetCard
          rounded="xl"
          className="xl:col-span-8"
          title={
            <CardTitle
              icon={<ResponseIcon aria-hidden className="size-5" />}
              tile={item.lastError ? "bg-red-lighter text-red-dark" : "bg-green-lighter text-green-dark"}
            >
              Tally&rsquo;s response
            </CardTitle>
          }
          headerClassName="items-center"
          description={
            <Text className="mt-2 text-sm text-gray-500">
              Shown exactly as Tally returned it — the exact wording identifies the ledger or voucher type to fix.
            </Text>
          }
        >
          <div className="mt-5">
            {item.lastError ? (
              <VerbatimErrorText label="Tally's response, verbatim">{item.lastError}</VerbatimErrorText>
            ) : (
              <div className="flex items-center gap-3 rounded-lg bg-green-lighter/40 px-4 py-3">
                <SentIcon aria-hidden className="size-5 shrink-0 text-green-dark" />
                <Text className="text-sm text-gray-700">Tally has not reported an error for this record.</Text>
              </div>
            )}
          </div>

          {mayRetry ? (
            <div className="mt-5 rounded-lg border border-s-4 border-muted border-s-secondary bg-secondary-lighter/30 px-4 py-3 dark:bg-secondary-lighter/10">
              <Text className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary-dark">
                <InfoIcon aria-hidden className="size-4" />
                {retryable ? "Retry this record" : "No retry"}
              </Text>
              {retryable ? (
                <Text className="mt-1 text-sm text-gray-700">
                  Use once the cause is fixed — for example, after creating the missing ledger or correcting the voucher type.
                  The attempt count is not reset. <strong className="font-semibold">Retry now</strong> is at the top of the page.
                </Text>
              ) : (
                <Text className="mt-1 text-sm text-gray-700">
                  This record has been posted to Tally. There is no retry, because retrying a posted voucher is how one gets
                  posted twice.
                </Text>
              )}
            </div>
          ) : null}
        </WidgetCard>

        <WidgetCard
          rounded="xl"
          className="xl:col-span-4"
          title={
            <CardTitle icon={<RecordIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
              Record
            </CardTitle>
          }
          headerClassName="items-center"
        >
          <div className="mt-5 flex flex-col gap-4">
            <InfoBlock icon={<ReferenceIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Reference">
              <Text className="text-sm text-gray-900">{item.entityReference ? (syncItemLabel(item) ?? "—") : "not yet referenced"}</Text>
            </InfoBlock>
            <InfoBlock icon={<TypeIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Type">
              <Text className="font-mono text-sm text-gray-900">{item.entityType}</Text>
            </InfoBlock>
            <InfoBlock icon={<Inbox aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Status">
              <Text className="font-mono text-sm text-gray-900">{item.status}</Text>
            </InfoBlock>
            <InfoBlock icon={<AttemptsIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Attempts">
              <Text className="text-sm text-gray-900">{Number.isNaN(item.attempts) ? "not reported" : item.attempts}</Text>
            </InfoBlock>

            <div className="border-t border-dashed border-muted" />

            <InfoBlock icon={<QueuedIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Queued (UTC)">
              <Text className="text-sm text-gray-900">{formatMoment(item.queuedAt)}</Text>
              {late ? <Text className="font-mono text-xs text-red-dark">over {LATENCY_FLOOR_MINUTES}m</Text> : null}
            </InfoBlock>
            <InfoBlock icon={<AttemptIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Last attempt (UTC)">
              <Text className="text-sm text-gray-900">{formatMoment(item.lastAttemptAt)}</Text>
            </InfoBlock>
          </div>

          <Link to="/integrations/tally" className="mt-5 inline-flex text-sm font-medium text-secondary hover:underline">
            Is the agent itself healthy? Open agent health
          </Link>
        </WidgetCard>
      </div>
    </div>
  );
}
